import type { Point2 } from './point';

/** Tolerance for treating a length as zero, in drawing units. */
const EPS = 1e-9;

/**
 * How many full pattern cycles a line must hold before it is dashed at all.
 * Shorter lines are drawn continuous — see the short-line rule in
 * `expandDashPattern`.
 *
 * AutoCAD documents this threshold as one full pattern, which is the default here.
 * eDrawings appears to use a higher effective threshold: on a reference drawing it
 * draws ~1.8-cycle hidden lines solid and only dashes lines several cycles long.
 * Raising this to 2 reproduces that. It is deliberately a named constant rather
 * than a magic `1` because it is the one genuinely uncertain quantity in the dash
 * model — the expansion itself is verified against the pattern.
 *
 * NB: if our pattern cycle is itself too small (i.e. the linetype scale applied
 * upstream is wrong), raising this only masks that. The two are distinguishable by
 * comparing absolute dash length against another viewer at the same zoom, not by
 * counting dashes.
 */
const MIN_PATTERN_CYCLES_TO_DASH = 1;

/** A-alignment needs a dash and a gap; a single descriptor has nothing to phase. */
const MIN_PATTERN_ELEMENTS_FOR_ALIGNMENT = 2;

/**
 * Flattened output of a dash expansion.
 *
 * `segments` is a flat `[x1,y1,z1, x2,y2,z2, …]` list of *drawn* line-segment
 * endpoint pairs, ready for a `LineSegments` position attribute. `dots` is a flat
 * `[x,y,z, …]` list of isolated dot positions for a `Points` attribute.
 *
 * Both are appended to, so many entities can accumulate into one batched buffer.
 * The z components are always 0 — the consumer is a flat drawing on the z=0 plane —
 * but are emitted so the buffers drop straight into a three.js position attribute
 * without a repacking pass.
 */
export type DashExpansion = {
  segments: number[];
  dots: number[];
};

/** Even indices are pen-down; odd indices are gaps. */
const isDrawnElement = (index: number): boolean => index % 2 === 0;

/** Total length of a polyline in drawing units. */
const totalPolylineLength = (points: readonly Point2[]): number => {
  let total = 0;
  for (let i = 0; i < points.length - 1; i += 1) {
    total += Math.hypot(
      points[i + 1][0] - points[i][0],
      points[i + 1][1] - points[i][1],
    );
  }
  return total;
};

const appendSolidPolyline = (
  points: readonly Point2[],
  segments: number[],
): void => {
  for (let i = 0; i < points.length - 1; i += 1) {
    const [ax, ay] = points[i];
    const [bx, by] = points[i + 1];
    segments.push(ax, ay, 0, bx, by, 0);
  }
};

/**
 * AutoCAD A-alignment end-dash length.
 *
 * The first descriptor is reserved for both endpoints: each end gets half of
 * that dash, plus half of whatever is left after packing whole cycles into the
 * line. Interior dashes stay at their defined length; only the two caps grow or
 * shrink (never below half the first dash).
 *
 * That is what eDrawings uses, and why tiling the pattern from the start point
 * looks phase-shifted against it — leftover used to pile up at one end.
 */
const alignedEndDashLength = (
  firstDash: number,
  polylineLength: number,
  patternTotal: number,
): number => {
  const leftover =
    polylineLength - Math.floor(polylineLength / patternTotal) * patternTotal;
  return (firstDash + leftover) / 2;
};

type DashPhase = 'start' | 'middle' | 'end';

type PatternWalker = {
  phase: DashPhase;
  elementIndex: number;
  isPenDown: boolean;
  remaining: number;
};

/** Move from the start cap into the interior, or to the next interior element. */
const nextPatternElement = (
  walker: PatternWalker,
  pattern: readonly number[],
): PatternWalker => {
  const elementIndex =
    walker.phase === 'start' ? 1 : (walker.elementIndex + 1) % pattern.length;
  return {
    phase: 'middle',
    elementIndex,
    isPenDown: isDrawnElement(elementIndex),
    remaining: Math.abs(pattern[elementIndex]),
  };
};

export type ExpandDashPatternOptions = {
  /**
   * AutoCAD `$PLINEGEN=0`: the pattern restarts at every vertex, each span is
   * A-aligned on its own, and a span shorter than one cycle is drawn solid.
   *
   * Default is continuous (pen state carries through corners). That is required
   * for flattened CIRCLE/ARC/SPLINE payloads, whose tessellation vertices are
   * not real DXF vertices — a per-span short-line test would de-dash every arc.
   * LINE / POLYLINE / LWPOLYLINE vertices *are* real, which is why the renderer
   * turns this on for those types.
   */
  readonly restartAtVertices?: boolean;
};

/**
 * Expands a polyline into explicit dash segments (and dot positions) for a DXF
 * linetype pattern, measured in drawing units.
 *
 * The pattern is a positive sequence where **index parity sets the pen state**:
 * even index = drawn, odd index = gap. So `[12.7, 2.54, 2.54, 2.54, 0]` (CENTER)
 * reads long-dash, gap, short-dash, gap, dot.
 *
 * Parity rather than a per-element toggle is what makes odd-length patterns tile
 * correctly: on the wrap from the last element back to index 0 the state is
 * recomputed from the index, so an odd-length pattern doesn't invert every pass.
 *
 * By default pen state carries across vertices, so a dash spans a corner instead
 * of restarting at every vertex — which is precisely what a `LineDashedMaterial`
 * over batched `LineSegments` cannot do. Pass `restartAtVertices` to get
 * AutoCAD's per-segment PLINEGEN=0 behaviour instead.
 *
 * Phase follows AutoCAD A-alignment (the only alignment DXF defines, and the one
 * eDrawings uses): the line starts and ends with a dash of at least half the
 * first descriptor, leftover length is split onto those two caps, and between
 * them the pattern runs from the *second* descriptor, wrapping through the first
 * as an interior dash.
 *
 * An empty or all-zero pattern yields a solid polyline, and so does a polyline
 * shorter than one full pattern cycle (see the short-line rule below).
 *
 * A `0` at an even (drawn) index is a dot, but only emitted when it is genuinely
 * isolated — i.e. the next element is a gap. A zero that abuts another drawn
 * element (CENTER's trailing `0`, which wraps straight into the long dash) would
 * just blot the start of that dash, so it is skipped.
 */
export const expandDashPattern = (
  points: readonly Point2[],
  pattern: readonly number[],
  out: DashExpansion = { segments: [], dots: [] },
  options: ExpandDashPatternOptions = {},
): DashExpansion => {
  if (options.restartAtVertices && points.length > 2) {
    for (let i = 0; i < points.length - 1; i += 1) {
      expandDashPattern([points[i], points[i + 1]], pattern, out);
    }
    return out;
  }

  const { segments, dots } = out;

  const patternTotal = pattern.reduce((sum, value) => sum + Math.abs(value), 0);
  const hasPattern = pattern.length > 0 && patternTotal > EPS;
  const polylineLength = totalPolylineLength(points);

  // CAD's short-line rule: a line too short to hold one full pattern cycle is
  // drawn continuous rather than given an arbitrary slice of the pattern. Without
  // this, short edges (the connectors on a counterbore symbol, say) render as a
  // stub or vanish entirely, and the breaks land in visibly arbitrary places.
  //
  // Measured over the whole polyline, not per segment, unless the caller passed
  // `restartAtVertices`. Curves arrive already flattened into many short
  // segments, so a per-segment test on a CIRCLE/ARC would silently de-dash every
  // arc; the entity's total length is the flattening-agnostic quantity. Real
  // POLYLINE vertices are a different story — see `restartAtVertices`.
  const isSolid =
    !hasPattern ||
    pattern.length < MIN_PATTERN_ELEMENTS_FOR_ALIGNMENT ||
    polylineLength < patternTotal * MIN_PATTERN_CYCLES_TO_DASH - EPS;

  if (isSolid) {
    appendSolidPolyline(points, segments);
    return out;
  }

  const firstDash = Math.abs(pattern[0]);
  const endDashLength = alignedEndDashLength(
    firstDash,
    polylineLength,
    patternTotal,
  );
  const middleEnd = polylineLength - endDashLength;

  let phase: DashPhase = 'start';
  let elementIndex = 0;
  let isPenDown = true;
  let remaining = endDashLength;
  let distanceAlong = 0;

  for (let i = 0; i < points.length - 1; i += 1) {
    const [ax, ay] = points[i];
    const [bx, by] = points[i + 1];
    const deltaX = bx - ax;
    const deltaY = by - ay;
    const segmentLength = Math.hypot(deltaX, deltaY);
    if (segmentLength <= EPS) {
      continue;
    }
    const unitX = deltaX / segmentLength;
    const unitY = deltaY / segmentLength;

    let consumed = 0;
    let cursorX = ax;
    let cursorY = ay;

    while (consumed < segmentLength - EPS) {
      if (distanceAlong >= polylineLength - EPS) {
        break;
      }

      if (phase === 'middle' && distanceAlong >= middleEnd - EPS) {
        phase = 'end';
        isPenDown = true;
        remaining = polylineLength - distanceAlong;
        continue;
      }

      if (remaining <= EPS) {
        // A zero-length drawn element is a dot, but only a real one: isolated
        // between two gaps. Finishing a positive dash is handled below and must
        // not land here, or every dash end would sprout a blot.
        const nextIsDrawn =
          phase === 'end'
            ? false
            : phase === 'start'
            ? isDrawnElement(1)
            : isDrawnElement((elementIndex + 1) % pattern.length);
        if (isPenDown && !nextIsDrawn) {
          dots.push(cursorX, cursorY, 0);
        }
        if (phase === 'end') {
          break;
        }
        ({ phase, elementIndex, isPenDown, remaining } = nextPatternElement(
          { phase, elementIndex, isPenDown, remaining },
          pattern,
        ));
        continue;
      }

      const distanceToEndDash = middleEnd - distanceAlong;
      const step = Math.min(
        remaining,
        segmentLength - consumed,
        phase === 'middle' ? Math.max(distanceToEndDash, 0) : Infinity,
      );
      if (step <= EPS) {
        break;
      }

      const nextX = cursorX + unitX * step;
      const nextY = cursorY + unitY * step;
      if (isPenDown) {
        segments.push(cursorX, cursorY, 0, nextX, nextY, 0);
      }
      cursorX = nextX;
      cursorY = nextY;
      consumed += step;
      distanceAlong += step;
      remaining -= step;
      if (remaining <= EPS && phase !== 'end') {
        ({ phase, elementIndex, isPenDown, remaining } = nextPatternElement(
          { phase, elementIndex, isPenDown, remaining },
          pattern,
        ));
      }
    }
  }

  return out;
};
